import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { useConfigurationStore } from '@store/use-configuration-store';
import { useElearningWpRemoteStore } from '@store/use-ewr-store';
import { useUserStore } from '@store/use-user-store';

import { ROUTES } from '../constants/routes';
import type { ApiError } from '../types';
import { apis } from './api/Api';

export type {
  ElearningWpRemoteTestAnswer,
  ElearningWpRemoteTestQuestion,
  ElearningWpRemoteTestSubmitResponse
} from './elearning-wp-remote';

function notifyError(err: ApiError) {
  notification.error({
    title: err?.response?.data?.errorMessage ?? 'Something went wrong',
    placement: 'topRight'
  });
}

export const useExternalElearningPersonInfoQuery = (friendlyUrl: string) =>
  useQuery({
    queryKey: ['eel-person-info', friendlyUrl],
    queryFn: () =>
      apis.externalElearning.getPersonInfo(friendlyUrl).then((r) => r.data),
    enabled: !!friendlyUrl,
    refetchOnMount: 'always'
  });

export const useExternalElearningUpdatePersonMutation = (
  friendlyUrl: string,
  contactId: string | null
) =>
  useMutation({
    mutationFn: (body: unknown) =>
      apis.externalElearning.updatePerson(
        friendlyUrl,
        contactId as string,
        body
      ),
    onError: notifyError
  });

export const useExternalElearningDataProtectionDeclineMutation = (
  friendlyUrl: string,
  contactId: string | null
) =>
  useMutation({
    mutationFn: () =>
      apis.externalElearning.dataProtectionDecline(
        friendlyUrl,
        contactId as string,
        {
          decline: true
        }
      ),
    onError: notifyError
  });

export const useExternalElearningElearningAccept = () => {
  const { friendly_url } = useConfigurationStore();
  const { ewrContactId } = useElearningWpRemoteStore();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accepted: boolean) =>
      apis.externalElearning.elearningAccept(
        friendly_url,
        ewrContactId as string,
        {
          elearningAccept: accepted
        }
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['eel-test', friendly_url, ewrContactId]
      });
    },
    onError: notifyError
  });
};

export const useExternalElearningFinish = () => {
  const { friendly_url } = useConfigurationStore();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => apis.externalElearning.finish(friendly_url),
    onSuccess: () => navigate(ROUTES.EEL_FINAL),
    onError: notifyError
  });
};

export const useExternalElearningTestQuery = () => {
  const { friendly_url } = useConfigurationStore();
  const { ewrContactId } = useElearningWpRemoteStore();
  return useQuery({
    queryKey: ['eel-test', friendly_url, ewrContactId],
    queryFn: () =>
      apis.externalElearning
        .getElearningTest(friendly_url, ewrContactId as string)
        .then((r) => r.data.testQuestions),
    enabled: !!ewrContactId
  });
};

interface SubmitTestBody {
  testAnswers: { questionId: number; answerToken: string }[];
}

export const useExternalElearningSubmitTest = () => {
  const { friendly_url } = useConfigurationStore();
  const { ewrContactId } = useElearningWpRemoteStore();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SubmitTestBody) =>
      apis.externalElearning
        .submitElearningTest(friendly_url, ewrContactId as string, body)
        .then((r) => r.data),
    onSuccess: async (result: { status: string }) => {
      if (result.status === 'fail') {
        await queryClient.invalidateQueries({
          queryKey: ['eel-test', friendly_url, ewrContactId]
        });
      }
    },
    onError: notifyError
  });
};

export const useExternalElearningResendOtp = () => {
  const { friendly_url } = useConfigurationStore();
  return useMutation({
    mutationFn: () => apis.externalElearning.generateOtp(friendly_url),
    onError: notifyError
  });
};

export const useExternalElearningVerifyOtp = () => {
  const { friendly_url } = useConfigurationStore();
  const { accessToken, refreshToken, loginSuccess } = useUserStore();
  return useMutation({
    mutationFn: (otp: string) =>
      apis.externalElearning.verifyOtp(friendly_url, { otp }),
    onSuccess: () => {
      loginSuccess({
        username: friendly_url,
        password: '',
        accessToken,
        refreshToken
      });
    },
    onError: notifyError
  });
};
